import { Uint64 } from "./uint64";

export type DetectedCircleInfoGPU = {
    x: number;
    y: number;
    w: number;
    h: number;
    v: number;
    cb: number;
    cr: number;
    pad: number;
};

export default class StickerInfoGpu3x3 {
    code: Uint64;
    big_code: bigint;

    xtypex: number;
    debug_stage: number;

    blob_list: number[];
    circle_colors: number[];
    circle_spins: number[];

    detected_circle_info: DetectedCircleInfoGPU[];

    sticker_direction: { x: number; y: number };
    debug_fail_blob: number;
    pad0: number;

    parsed_index: number;

    stride: number;       // in u32 words
    stride_bytes: number; // in bytes

    m: number[];

    constructor() {
        this.code = { low: 0, high: 0 };
        this.big_code = 0n;

        this.xtypex = 0;
        this.debug_stage = 0;

        this.blob_list = new Array(10).fill(0);
        this.circle_colors = new Array(10).fill(0);
        this.circle_spins = new Array(10).fill(0);

        this.detected_circle_info = [];
        for (let i = 0; i < 10; i++) {
            this.detected_circle_info.push({
                x: 0,
                y: 0,
                w: 0,
                h: 0,
                v: 0,
                cb: 0,
                cr: 0,
                pad: 0,
            });
        }

        this.sticker_direction = { x: 0, y: 0 };
        this.debug_fail_blob = 0;
        this.pad0 = 0;

        this.parsed_index = 0;

        this.stride = 118;
        this.stride_bytes = 472;

        this.m = new Array(10).fill(0);
        this.m[0] = 2;
        this.m[1] = 3;
        this.m[2] = 4;
        this.m[3] = 5;
        this.m[4] = 6;
        this.m[5] = 7;
        this.m[6] = 8;
        this.m[7] = 1;
        this.m[8] = 0;
    }

    get_buffer_size(count: number) {
        return count * this.stride_bytes;
    }

    u64FromU32Pair(high: number, low: number): bigint {
        const hi = BigInt(high >>> 0);
        const lo = BigInt(low >>> 0);
        return (hi << 32n) | lo;
    }

    get_sticker_type(parsed_index: number, buf: Uint32Array) {
        const index = parsed_index * this.stride;
        return buf[index + 2];
    }

    get_debug_stage(parsed_index: number, buf: Uint32Array) {
        const index = parsed_index * this.stride;
        return buf[index + 3];
    }

    get_code(parsed_index: number, buf: Uint32Array) {
        const index = parsed_index * this.stride;
        const code_low = buf[index];
        const code_high = buf[index + 1];
        return this.u64FromU32Pair(code_high, code_low);
    }

    readf32(words: Uint32Array, index: number): number {
        const f32 = new Float32Array(words.buffer, words.byteOffset, words.length);
        return f32[index];
    }

    parse(parsed_index: number, buf: Uint32Array) {
        this.parsed_index = parsed_index;
        let index = parsed_index * this.stride;

        this.code.low = buf[index]; index++;
        this.code.high = buf[index]; index++;
        this.big_code = this.u64FromU32Pair(this.code.high, this.code.low);

        this.xtypex = buf[index]; index++;
        this.debug_stage = buf[index]; index++;

        for (let i = 0; i < 10; i++) {
            this.blob_list[i] = buf[index]; index++;
        }

        for (let i = 0; i < 10; i++) {
            this.circle_colors[i] = buf[index]; index++;
        }

        for (let i = 0; i < 10; i++) {
            this.circle_spins[i] = buf[index]; index++;
        }

        for (let i = 0; i < 10; i++) {
            this.detected_circle_info[i].x = buf[index]; index++;
            this.detected_circle_info[i].y = buf[index]; index++;
            this.detected_circle_info[i].w = buf[index]; index++;
            this.detected_circle_info[i].h = buf[index]; index++;
            this.detected_circle_info[i].v = buf[index]; index++;
            this.detected_circle_info[i].cb = buf[index]; index++;
            this.detected_circle_info[i].cr = buf[index]; index++;
            this.detected_circle_info[i].pad = buf[index]; index++;
        }

        this.sticker_direction.x = this.readf32(buf, index); index++;
        this.sticker_direction.y = this.readf32(buf, index); index++;

        this.debug_fail_blob = buf[index]; index++;
        this.pad0 = buf[index]; index++;
    }

    print(prefix: string) {
        let colorlist = "";
        let spinlist = "";
        let blobs = "";
        let dcis = "";

        for (let i = 0; i < 9; i++) {
            const mi = i;
            blobs += this.blob_list[mi].toString() + ", ";
            colorlist += this.circle_colors[mi].toString() + ", ";
            spinlist += this.circle_spins[mi].toString() + ", ";

            const d = this.detected_circle_info[mi];
            dcis += `{${d.x},${d.y},${d.w},${d.h},${d.v},${d.cb},${d.cr}}, `;
        }

        console.log(
            "%s - %d - low:%d high:%d xtypex:%d debug_stage:%d big_code:%s\nblobs: %s\ncolors: %s\nspins: %s\ndetected: %s\ndirection: {%f,%f}\ndebug_fail_blob:%d",
            prefix,
            this.parsed_index,
            this.code.low,
            this.code.high,
            this.xtypex,
            this.debug_stage,
            this.big_code.toString(),
            blobs,
            colorlist,
            spinlist,
            dcis,
            this.sticker_direction.x,
            this.sticker_direction.y,
            this.debug_fail_blob
        );
    }

    log(line_number: number) {
        // optional debug hook
    }
}